/**
 * Opportunities — full-CRM pipeline.
 *
 * Top-level page:
 *   - KPI strip (count · raw pipeline · weighted pipeline · won/lost)
 *   - Kanban board (8 stages)
 *   - "New opportunity" create modal with LinkedIn auto-fill for both
 *     the counterparty (company LinkedIn) and primary contact
 *
 * The per-opportunity detail drawer (Overview / Contacts / Activities /
 * Tasks / Files / Lost) lives in `OpportunityDetail.jsx`.
 */
import React, { useEffect, useState } from 'react';
import { Plus, X, TrendingUp, Trophy, AlertOctagon, Clock, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import LinkedInButton from '@/components/LinkedInButton';
import LinkedInAutofillField from '@/components/LinkedInAutofillField';
import PhoneInput from '@/components/PhoneInput';
import Avatar from '@/components/Avatar';
import OpportunityDetail from './OpportunityDetail';

const STAGES = [
  { id: 'inbound',       label: 'Inbound',       color: '#94A3B8' },
  { id: 'triage',        label: 'Triage',        color: '#64748B' },
  { id: 'due_diligence', label: 'Due Diligence', color: '#0EA5E9' },
  { id: 'term_sheet',    label: 'Term Sheet',    color: '#8B3FC5' },
  { id: 'approved',      label: 'Approved',      color: '#5E1F8B' },
  { id: 'executed',      label: 'Executed',      color: '#0A0A0A' },
  { id: 'delivered',     label: 'Delivered',     color: '#059669' },
  { id: 'closed_lost',   label: 'Closed Lost',   color: '#EF4444' },
];

const TYPES = [
  { id: 'investment', label: 'Investment' },
  { id: 'partnership', label: 'Partnership' },
  { id: 'sponsorship', label: 'Sponsorship' },
  { id: 'acquisition', label: 'Acquisition' },
  { id: 'vendor', label: 'Vendor' },
  { id: 'other', label: 'Other' },
];

const SOURCES = [
  { id: 'referral',  label: 'Referral' },
  { id: 'inbound',   label: 'Inbound' },
  { id: 'outbound',  label: 'Outbound' },
  { id: 'event',     label: 'Event / Conference' },
  { id: 'partner',   label: 'Partner channel' },
  { id: 'website',   label: 'Website / form' },
  { id: 'other',     label: 'Other' },
];

function fmtMoney(n) {
  if (n == null) return '0';
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return n.toString();
}

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch + drag effects. */
export default function Opportunities() {
  const [board, setBoard] = useState(null);
  const [stuck, setStuck] = useState(null);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null);
  // Drag-and-drop kanban state
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  // Lost-reason inline modal when a card is dragged to closed_lost
  const [pendingLost, setPendingLost] = useState(null); // {opportunity_id}
  const blank = {
    title: '', counterparty_name: '', counterparty_linkedin_url: '',
    counterparty_picture: null, counterparty_email: '',
    type: 'investment', sector: '', description: '',
    deal_size: '', currency: 'QAR', expected_close_date: '',
    source_category: '', source_detail: '',
    primary_contact_name: '', primary_contact_email: '',
    primary_contact_phone: '', primary_contact_phone_country: null,
    primary_contact_linkedin_url: '', primary_contact_picture: null,
  };
  const [form, setForm] = useState(blank);

  const load = async () => {
    try {
      const { data } = await api.get('/opportunities/board');
      setBoard(data);
    } catch (err) {
      console.error('Opportunities: board load failed', err);
      toast.error('Could not load opportunities');
    }
    try {
      const { data } = await api.get('/opportunities/stuck', { params: { days: 14 } });
      setStuck(data);
    } catch (err) {
      // Non-critical widget — log but don't toast.
      console.warn('Opportunities: stuck widget unavailable', err);
    }
  };
  useEffect(() => { load(); }, []);

  // ─── Drag and drop ───────────────────────────
  const onDragStart = (opportunityId) => (e) => {
    setDraggingId(opportunityId);
    try {
      e.dataTransfer.setData('text/plain', opportunityId);
      e.dataTransfer.effectAllowed = 'move';
    } catch (err) {
      // Some browsers / synthetic test envs throw on setData. Safe to ignore.
      console.warn('Opportunities: dataTransfer.setData failed', err);
    }
  };
  const onDragEnd = () => {
    setDraggingId(null);
    setDragOverStage(null);
  };
  const onDragOver = (stageId) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverStage !== stageId) setDragOverStage(stageId);
  };
  const onDragLeave = (stageId) => () => {
    if (dragOverStage === stageId) setDragOverStage(null);
  };
  const onDrop = (stageId) => async (e) => {
    e.preventDefault();
    const opportunityId = e.dataTransfer?.getData('text/plain') || draggingId;
    setDragOverStage(null);
    setDraggingId(null);
    if (!opportunityId) return;
    // Find current stage from board state to avoid no-op API calls.
    const current = Object.entries(board?.grouped || {}).find(([, items]) =>
      items.some((o) => o.opportunity_id === opportunityId),
    )?.[0];
    if (current === stageId) return;
    if (stageId === 'closed_lost') {
      // Lost moves require a taxonomy reason — open inline modal instead
      // of firing the stage endpoint.
      setPendingLost({ opportunity_id: opportunityId });
      return;
    }
    try {
      await api.post('/opportunities/stage', { opportunity_id: opportunityId, stage: stageId });
      toast.success(`Moved to ${stageId.replace(/_/g, ' ')}`);
      load();
    } catch (err) {
      toast.error('Stage move failed');
    }
  };

  const confirmLost = async (reason, note) => {
    if (!pendingLost) return;
    try {
      await api.post(`/opportunities/${pendingLost.opportunity_id}/lost`,
        { reason, note: note || null });
      toast.success(`Marked lost · ${reason}`);
      setPendingLost(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const handleCompanyAutofill = (fields) => {
    setForm((prev) => ({
      ...prev,
      counterparty_name: prev.counterparty_name || fields.name || '',
      counterparty_picture: prev.counterparty_picture || fields.picture_url || null,
      sector: prev.sector || fields.industry || '',
      description: prev.description || (
        [fields.tagline, fields.summary].filter(Boolean).join('\n\n') || ''
      ),
    }));
  };

  const handleContactAutofill = (fields) => {
    setForm((prev) => ({
      ...prev,
      primary_contact_name: prev.primary_contact_name || fields.name || '',
      primary_contact_picture: prev.primary_contact_picture || fields.picture_url || null,
    }));
  };

  const create = async (e) => {
    e.preventDefault();
    if (!form.title || !form.counterparty_name) {
      return toast.error('Title & counterparty are required.');
    }
    if (!form.primary_contact_name || !form.primary_contact_email || !form.primary_contact_phone) {
      return toast.error('Primary contact name, email and phone are required.');
    }
    try {
      const payload = {
        ...form,
        deal_size: form.deal_size ? parseFloat(form.deal_size) : null,
        expected_close_date: form.expected_close_date
          ? new Date(form.expected_close_date).toISOString() : null,
        source_category: form.source_category || null,
      };
      await api.post('/opportunities/', payload);
      toast.success('Opportunity logged in pipeline.');
      setOpen(false);
      setForm(blank);
      load();
    } catch { toast.error('Failed'); }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="ve-eyebrow">Opportunity CRM</div>
          <h1 className="display text-2xl sm:text-3xl md:text-4xl mt-2 font-bold">Funnel. Approve. Execute.</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Every incoming investment, partnership, and sponsorship — funneled through one pipeline.
            Contacts, activity history, tasks, files and lost reasons live with each deal so nothing slips.
          </p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="opp-create" className="ve-btn-primary self-start text-xs sm:text-sm">
          <Plus size={14}/> New opportunity
        </button>
      </div>

      {board && (
        <>
          {/* KPI STRIP */}
          <div className="mt-6 sm:mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3" data-testid="opp-kpi-strip">
            <div className="ve-card p-3 sm:p-4">
              <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">Active</div>
              <div className="display text-2xl sm:text-3xl mt-2 font-bold">{board.totals.count}</div>
            </div>
            <div className="ve-card p-3 sm:p-4 border-[#8B3FC5] bg-gradient-to-br from-[#FFF8E5] to-white">
              <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">Pipeline</div>
              <div className="display text-2xl sm:text-3xl mt-2 font-bold text-[#5E1F8B] truncate">
                {fmtMoney(board.totals.pipeline_value)}
              </div>
            </div>
            <div className="ve-card p-3 sm:p-4 col-span-2 sm:col-span-1" data-testid="opp-kpi-weighted">
              <div className="flex items-center gap-1 text-[9px] sm:text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
                <TrendingUp size={11}/> Weighted
              </div>
              <div className="display text-2xl sm:text-3xl mt-2 font-bold truncate">
                {fmtMoney(board.totals.weighted_pipeline_value)}
              </div>
              <div className="text-[9px] uppercase tracking-[0.18em] text-slate-400 mt-1">
                Size × win-probability
              </div>
            </div>
            <div className="ve-card p-3 sm:p-4 border-emerald-200 bg-emerald-50/40">
              <div className="flex items-center gap-1 text-[9px] sm:text-[10px] uppercase tracking-[0.22em] text-emerald-700 font-bold">
                <Trophy size={11}/> Won
              </div>
              <div className="display text-2xl sm:text-3xl mt-2 font-bold text-emerald-700">{board.totals.won_count}</div>
            </div>
            <div className="ve-card p-3 sm:p-4 border-rose-200 bg-rose-50/40">
              <div className="flex items-center gap-1 text-[9px] sm:text-[10px] uppercase tracking-[0.22em] text-rose-700 font-bold">
                <AlertOctagon size={11}/> Lost
              </div>
              <div className="display text-2xl sm:text-3xl mt-2 font-bold text-rose-700">{board.totals.lost_count}</div>
            </div>
          </div>

          {/* STUCK DEALS WIDGET — appears only when there are actually stuck deals */}
          {stuck && stuck.count > 0 && (
            <div className="mt-8 ve-card p-5 border-amber-300 bg-gradient-to-r from-amber-50/60 to-white"
                 data-testid="stuck-deals-widget">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-amber-700"/>
                  <h3 className="display text-base font-bold">
                    {stuck.count} stuck deal{stuck.count === 1 ? '' : 's'} going cold
                  </h3>
                  <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-700">
                    no activity in {stuck.threshold_days}+ days
                  </span>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2"
                   data-testid="stuck-deals-grid">
                {stuck.deals.slice(0, 6).map((d) => (
                  <button
                    key={d.opportunity_id}
                    onClick={() => {
                      // open the detail drawer for this deal
                      const opp = Object.values(board?.grouped || {}).flat()
                        .find((o) => o.opportunity_id === d.opportunity_id);
                      if (opp) setView(opp);
                    }}
                    data-testid={`stuck-deal-${d.opportunity_id}`}
                    className="text-left ve-card p-3 hover:border-amber-400 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-amber-700">
                        idle {d.idle_days}d
                      </span>
                      {d.deal_size != null && (
                        <span className="text-xs font-mono text-[#5E1F8B] font-bold">
                          {fmtMoney(d.deal_size)} {d.currency || ''}
                        </span>
                      )}
                    </div>
                    <div className="text-sm font-bold mt-1 line-clamp-1">{d.title}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {d.counterparty_name} · {(d.stage || '').replace(/_/g, ' ')}
                    </div>
                  </button>
                ))}
              </div>
              {stuck.count > 6 && (
                <div className="mt-2 text-[10px] uppercase tracking-[0.18em] text-amber-700 font-bold">
                  + {stuck.count - 6} more stuck deals on the board below
                </div>
              )}
            </div>
          )}

          {/* Kanban */}
          <div className="mt-8 flex gap-4 overflow-x-auto pb-4">
            {STAGES.map((s) => {
              const items = board.grouped[s.id] || [];
              const isHotDrop = dragOverStage === s.id && draggingId;
              return (
                <div
                  key={s.id}
                  data-testid={`opp-col-${s.id}`}
                  onDragOver={onDragOver(s.id)}
                  onDragLeave={onDragLeave(s.id)}
                  onDrop={onDrop(s.id)}
                  className={`w-72 shrink-0 rounded-lg transition-colors ${
                    isHotDrop ? 'bg-[#FFF8E5]/70 ring-2 ring-[#8B3FC5] ring-offset-2' : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-3 px-1">
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full" style={{ background: s.color }}/>
                      <h3 className="text-[11px] uppercase tracking-[0.22em] font-bold">{s.label}</h3>
                    </div>
                    <span className="text-xs text-slate-500 font-mono">{items.length}</span>
                  </div>
                  <div className="space-y-2 min-h-[120px]">
                    {items.length === 0 && (
                      <div className={`text-[11px] border border-dashed rounded-md p-3 text-center transition-colors ${
                        isHotDrop ? 'border-[#8B3FC5] text-[#5E1F8B] bg-white' : 'border-slate-200 text-slate-400'
                      }`}>
                        {isHotDrop ? 'Drop here' : 'Empty'}
                      </div>
                    )}
                    {items.map((o) => (
                      <button
                        key={o.opportunity_id}
                        data-testid={`opp-card-${o.opportunity_id}`}
                        draggable
                        onDragStart={onDragStart(o.opportunity_id)}
                        onDragEnd={onDragEnd}
                        onClick={() => setView(o)}
                        className={`w-full text-left ve-card p-3 hover:border-[#8B3FC5] hover:-translate-y-0.5 transition-all cursor-grab active:cursor-grabbing ${
                          draggingId === o.opportunity_id ? 'opacity-40' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{o.type}</span>
                          <div className="flex items-center gap-2 shrink-0">
                            {o.probability != null && (
                              <span className="text-[9px] uppercase tracking-[0.18em] text-[#5E1F8B] font-bold">
                                {o.probability}%
                              </span>
                            )}
                            {o.deal_size != null && (
                              <span className="text-xs font-mono text-[#5E1F8B] font-bold">{fmtMoney(o.deal_size)}</span>
                            )}
                          </div>
                        </div>
                        <div className="text-sm font-bold mt-1 line-clamp-2">{o.title}</div>
                        <div className="text-xs text-slate-500 mt-1 line-clamp-1 flex items-center gap-1.5">
                          {o.counterparty_name}
                          <LinkedInButton url={o.counterparty_linkedin_url} size={10}/>
                        </div>
                        {/* Open task count + overdue badge */}
                        {(() => {
                          const open = (o.tasks || []).filter((t) => t.status === 'open');
                          const overdue = open.filter((t) =>
                            t.due_date && new Date(t.due_date) < new Date());
                          if (open.length === 0) return null;
                          return (
                            <div className="mt-2 flex gap-1.5">
                              <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">
                                {open.length} open task{open.length === 1 ? '' : 's'}
                              </span>
                              {overdue.length > 0 && (
                                <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-rose-700 bg-rose-50 px-1.5 py-0.5 rounded">
                                  {overdue.length} overdue
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Create modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
             onClick={(e) => e.target === e.currentTarget && setOpen(false)}>
          <form onSubmit={create} className="ve-card p-7 w-full max-w-3xl bg-white max-h-[88vh] overflow-y-auto"
                data-testid="opp-create-form">
            <div className="flex items-center justify-between">
              <h2 className="display text-2xl font-bold">New opportunity</h2>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700">
                <X size={18}/>
              </button>
            </div>

            {/* COUNTERPARTY LinkedIn auto-fill */}
            <div className="mt-6">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-2">
                Counterparty LinkedIn — paste a company URL and we&apos;ll prefill name + sector + description + logo
              </div>
              <div className="flex items-center gap-3">
                <Avatar
                  src={form.counterparty_picture}
                  email={form.counterparty_email}
                  name={form.counterparty_name || 'Co'}
                  size={48}
                  testId="opp-counterparty-avatar"
                />
                <div className="flex-1">
                  <LinkedInAutofillField
                    variant="company"
                    value={form.counterparty_linkedin_url}
                    onChange={(v) => setForm({ ...form, counterparty_linkedin_url: v })}
                    onAutofill={handleCompanyAutofill}
                    testId="opp-counterparty-linkedin"
                  />
                </div>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="md:col-span-2 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Title *
                <input required data-testid="opp-title" value={form.title}
                       onChange={(e) => setForm({...form, title: e.target.value})}
                       className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] normal-case tracking-normal font-normal"/>
              </label>
              <label className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Counterparty *
                <input required data-testid="opp-counterparty" value={form.counterparty_name}
                       onChange={(e) => setForm({...form, counterparty_name: e.target.value})}
                       className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] normal-case tracking-normal font-normal"/>
              </label>
              <label className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Type
                <select data-testid="opp-type" value={form.type}
                        onChange={(e) => setForm({...form, type: e.target.value})}
                        className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] normal-case tracking-normal font-normal">
                  {TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </label>
              <label className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Sector
                <input data-testid="opp-sector" value={form.sector}
                       onChange={(e) => setForm({...form, sector: e.target.value})}
                       className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] normal-case tracking-normal font-normal"/>
              </label>
              <label className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Deal size
                <input type="number" step="any" data-testid="opp-deal-size" value={form.deal_size}
                       onChange={(e) => setForm({...form, deal_size: e.target.value})}
                       className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] font-normal"/>
              </label>
              <label className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Currency
                <input value={form.currency}
                       onChange={(e) => setForm({...form, currency: e.target.value})}
                       className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] uppercase tracking-normal font-normal"/>
              </label>
              <label className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Expected close
                <input type="date" value={form.expected_close_date}
                       onChange={(e) => setForm({...form, expected_close_date: e.target.value})}
                       className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] font-normal"/>
              </label>
              <label className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Source category
                <select value={form.source_category}
                        onChange={(e) => setForm({...form, source_category: e.target.value})}
                        data-testid="opp-source-category"
                        className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] normal-case tracking-normal font-normal">
                  <option value="">— select —</option>
                  {SOURCES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </label>
              <label className="md:col-span-2 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Source detail
                <input value={form.source_detail}
                       onChange={(e) => setForm({...form, source_detail: e.target.value})}
                       placeholder="e.g. 'Introduced by Mohammed Al-Sayed at Doha Tech Week'"
                       className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] normal-case tracking-normal font-normal"/>
              </label>
              <label className="md:col-span-2 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Description
                <textarea rows={4} value={form.description}
                          onChange={(e) => setForm({...form, description: e.target.value})}
                          className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] normal-case tracking-normal font-normal"/>
              </label>
            </div>

            {/* PRIMARY CONTACT */}
            <div className="mt-6 border-t border-slate-200 pt-5">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-2">
                Primary contact — paste a LinkedIn URL and we&apos;ll prefill name + photo
              </div>
              <div className="flex items-center gap-3">
                <Avatar
                  src={form.primary_contact_picture}
                  email={form.primary_contact_email}
                  name={form.primary_contact_name || 'Contact'}
                  size={48}
                  testId="opp-contact-avatar"
                />
                <div className="flex-1">
                  <LinkedInAutofillField
                    value={form.primary_contact_linkedin_url}
                    onChange={(v) => setForm({ ...form, primary_contact_linkedin_url: v })}
                    onAutofill={handleContactAutofill}
                    testId="opp-contact-linkedin"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                <input placeholder="Contact name *" required value={form.primary_contact_name}
                       onChange={(e) => setForm({...form, primary_contact_name: e.target.value})}
                       data-testid="opp-primary-contact-name"
                       className="rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5]"/>
                <input type="email" placeholder="Email *" required value={form.primary_contact_email}
                       onChange={(e) => setForm({...form, primary_contact_email: e.target.value})}
                       data-testid="opp-primary-contact-email"
                       className="rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5]"/>
              </div>
              <div className="mt-3">
                <PhoneInput
                  value={form.primary_contact_phone}
                  country={form.primary_contact_phone_country}
                  required
                  onChange={(phone, iso) => setForm({
                    ...form, primary_contact_phone: phone,
                    primary_contact_phone_country: iso,
                  })}
                  testId="opp-primary-contact-phone"
                />
              </div>
              <p className="mt-2 text-[10px] text-slate-500">
                Name, email and phone are <b>mandatory</b> — every deal needs at least one reachable human.
              </p>
            </div>

            <div className="mt-7 flex justify-end gap-3">
              <button type="button" onClick={() => setOpen(false)} className="ve-btn-secondary">Cancel</button>
              <button type="submit" data-testid="opp-submit" className="ve-btn-primary">Create</button>
            </div>
          </form>
        </div>
      )}

      {/* Detail drawer */}
      {view && (
        <OpportunityDetail
          opp={view}
          onClose={() => { setView(null); load(); }}
          onChange={(updated) => { setView(updated); load(); }}
        />
      )}

      {/* Inline lost-reason modal — appears when a card is dragged to "Closed Lost" */}
      {pendingLost && (
        <LostReasonModal
          onCancel={() => setPendingLost(null)}
          onConfirm={confirmLost}
        />
      )}
    </div>
  );
}

// ─────────── Lost reason modal ───────────
function LostReasonModal({ onCancel, onConfirm }) {
  const REASONS = [
    { id: 'price',        label: 'Price' },
    { id: 'fit',          label: 'Product/strategic fit' },
    { id: 'timing',       label: 'Timing' },
    { id: 'competition',  label: 'Competition won' },
    { id: 'no_decision',  label: 'No decision' },
    { id: 'no_budget',    label: 'No budget' },
    { id: 'ghosted',      label: 'Ghosted' },
    { id: 'other',        label: 'Other' },
  ];
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
         onClick={(e) => e.target === e.currentTarget && onCancel()}
         data-testid="lost-modal">
      <div className="ve-card p-6 w-full max-w-lg bg-white border-rose-300">
        <div className="flex items-center justify-between">
          <h2 className="display text-xl font-bold flex items-center gap-2">
            <AlertOctagon size={18} className="text-rose-600"/>
            Why are we losing this deal?
          </h2>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-700">
            <X size={18}/>
          </button>
        </div>
        <p className="text-xs text-slate-600 mt-1">
          Pick a reason — we use this to improve win-rates over time.
        </p>
        <div className="mt-4 grid grid-cols-2 gap-2" data-testid="lost-modal-grid">
          {REASONS.map((r) => (
            <button
              key={r.id}
              onClick={() => setReason(r.id)}
              data-testid={`lost-modal-reason-${r.id}`}
              className={`px-3 py-2 text-[10px] uppercase tracking-[0.18em] font-bold rounded border transition-colors ${
                reason === r.id
                  ? 'bg-rose-500 text-white border-rose-500'
                  : 'bg-white text-slate-700 border-slate-200 hover:border-rose-300'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <textarea
          rows={2}
          placeholder="Notes (optional)"
          value={note} onChange={(e) => setNote(e.target.value)}
          className="mt-3 w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-rose-300"
        />
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onCancel} className="ve-btn-secondary text-xs">Cancel</button>
          <button
            onClick={() => onConfirm(reason, note)}
            disabled={!reason}
            data-testid="lost-modal-confirm"
            className="ve-btn-primary text-xs bg-rose-500 border-rose-500 text-white hover:bg-rose-600 disabled:opacity-50"
          >
            Mark lost
          </button>
        </div>
      </div>
    </div>
  );
}
